/**
 * Display settings drawer — a WordPress "Screen Options"-style panel for
 * per-user display preferences, available on every authenticated page.
 *
 * It holds **light/dark** (moved here from its own top-bar button, Dan
 * 2026-10-31) and **text size**. It's a font *scale* applied at the
 * document root (`<html>` font-size). Because Tailwind sizing is rem-based, that
 * one setting scales text, padding, and controls proportionally across the whole
 * app — so "make the names bigger" is a user preference, not a per-page tweak.
 *
 * Saved on the device (localStorage) — a shared front-desk monitor keeps the
 * setting regardless of who's logged in. Designed to grow: pagination, density,
 * and per-table column visibility are the natural next controls.
 *
 * Trigger: an icon button in the top bar, on every screen size (TopBar places
 * it), so phones keep the light/dark choice. It used to be a desktop-only
 * "Display" pill, and before that it was pinned over the page.
 */
import React, { useEffect, useState } from "react";
import { Moon, SlidersHorizontal, Sun, X } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder,
} from "../../utils/themeUtils";
import { Button } from "../ui";

const STORAGE_KEY = "displayFontScale";

const SCALES = [
  { key: "default", label: "Default", px: 16 },
  { key: "large", label: "Large", px: 18 },
  { key: "larger", label: "Larger", px: 20 },
] as const;

type ScaleKey = (typeof SCALES)[number]["key"];

function readScale(): ScaleKey {
  const v = localStorage.getItem(STORAGE_KEY);
  return SCALES.find((s) => s.key === v)?.key || "default";
}

function applyScale(key: ScaleKey) {
  const px = SCALES.find((s) => s.key === key)?.px ?? 16;
  // Root font-size = base rem. Tailwind's rem-based sizes scale off this.
  document.documentElement.style.fontSize = `${px}px`;
}

interface DisplaySettingsDrawerProps {
  /** the top bar's icon-button classes */
  triggerClassName?: string;
}

const DisplaySettingsDrawer: React.FC<DisplaySettingsDrawerProps> = ({ triggerClassName = "" }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState<ScaleKey>(() => readScale());

  // Apply the persisted scale on mount + whenever it changes.
  useEffect(() => {
    applyScale(scale);
  }, [scale]);

  // Esc closes the drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const choose = (key: ScaleKey) => {
    setScale(key);
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      /* private mode / quota — the in-memory scale still applies this session */
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClassName}
        aria-label="Display settings"
        title="Display: light or dark, text size"
      >
        <SlidersHorizontal className="w-5 h-5" />
      </button>

      {open && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/40 z-[90]"
            onClick={() => setOpen(false)}
          />
          {/* Drawer */}
          <div
            className={`fixed top-0 right-0 h-full w-80 max-w-[90vw] z-[100] shadow-2xl border-l flex flex-col ${getCardBackground(
              isDarkMode
            )} ${getLightBorder(isDarkMode)}`}
            role="dialog"
            aria-label="Display settings"
          >
            <div
              className={`px-4 py-3 border-b ${getLightBorder(
                isDarkMode
              )} flex items-start justify-between`}
            >
              <div>
                <div
                  className={`text-sm font-bold tracking-wide flex items-center gap-2 ${getPrimaryText(
                    isDarkMode
                  )}`}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Display
                </div>
                <div className={`text-xs ${getMutedText(isDarkMode)}`}>
                  Your personal view settings
                </div>
              </div>
              <Button
                type="button"
                variant="neutral"
                size="sm"
                iconOnly
                onClick={() => setOpen(false)}
                aria-label="Close display settings"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Light / dark */}
              <div>
                <div
                  className={`text-xs font-medium uppercase tracking-wide mb-2 ${getSecondaryText(
                    isDarkMode
                  )}`}
                >
                  Appearance
                </div>
                <div
                  className={`flex rounded-lg border overflow-hidden ${getLightBorder(
                    isDarkMode
                  )}`}
                >
                  {[
                    { dark: false, label: "Light", Icon: Sun },
                    { dark: true, label: "Dark", Icon: Moon },
                  ].map(({ dark, label, Icon }) => {
                    const active = isDarkMode === dark;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => {
                          if (!active) toggleTheme();
                        }}
                        aria-pressed={active}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-sm transition-colors ${
                          active
                            ? "bg-[var(--cc-action)] text-[var(--cc-action-text)] font-semibold"
                            : `${getSecondaryText(
                                isDarkMode
                              )} hover:bg-gray-100 dark:hover:bg-gray-700`
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Text size */}
              <div>
                <div
                  className={`text-xs font-medium uppercase tracking-wide mb-2 ${getSecondaryText(
                    isDarkMode
                  )}`}
                >
                  Text size
                </div>
                <div
                  className={`flex rounded-lg border overflow-hidden ${getLightBorder(
                    isDarkMode
                  )}`}
                >
                  {SCALES.map((s) => {
                    const active = scale === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => choose(s.key)}
                        aria-pressed={active}
                        className={`flex-1 px-2 py-2 text-center transition-colors ${
                          active
                            ? "bg-[var(--cc-action)] text-[var(--cc-action-text)]"
                            : `${getSecondaryText(
                                isDarkMode
                              )} hover:bg-gray-100 dark:hover:bg-gray-700`
                        }`}
                      >
                        {/* Explicit px so the preview "A" shows the relative
                            size regardless of the current root scale. */}
                        <span
                          className="font-semibold leading-none"
                          style={{ fontSize: `${s.px}px` }}
                        >
                          A
                        </span>
                        <span className="block text-[11px] mt-1">{s.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className={`text-xs mt-2 ${getMutedText(isDarkMode)}`}>
                  Makes text and controls larger across the whole app. Saved on
                  this device.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DisplaySettingsDrawer;
